import type { Language } from '../contexts/TranslationContext';
import { GUEST_QUESTION_MAX_LENGTH, type LandingQuestionOrigin } from './guestQuestion';

const STORAGE_KEY = 'palen.landing-question-draft';
const MAX_AGE_MS = 24 * 60 * 60 * 1000;

export interface LandingQuestionDraft {
  id: string;
  question: string;
  language: Language;
  createdAt: number;
  origin?: LandingQuestionOrigin;
}

function isValidDraft(value: unknown): value is LandingQuestionDraft {
  if (typeof value !== 'object' || value === null) return false;
  const draft = value as Record<string, unknown>;
  return typeof draft.id === 'string' && Boolean(draft.id)
    && typeof draft.question === 'string' && Boolean(draft.question.trim())
    && draft.question.length <= GUEST_QUESTION_MAX_LENGTH
    && (draft.language === 'fr' || draft.language === 'en')
    && (draft.origin === undefined || draft.origin === 'typed' || draft.origin === 'example')
    && typeof draft.createdAt === 'number' && Number.isFinite(draft.createdAt)
    && draft.createdAt <= Date.now() && Date.now() - draft.createdAt < MAX_AGE_MS;
}

/** Editing state only: restoring this draft never authorizes a question request. */
export class LandingQuestionDraftService {
  private memory: LandingQuestionDraft | null = null;
  private storageUnavailable = false;
  private navigationChecked = false;
  private readonly listeners = new Set<() => void>();

  read(): LandingQuestionDraft | null {
    this.prepareNavigation();
    if (this.storageUnavailable) return this.readMemory();
    try {
      const raw = window.sessionStorage.getItem(STORAGE_KEY);
      let value: unknown = null;
      try { value = raw ? JSON.parse(raw) : null; } catch { /* Invalid drafts are discarded below. */ }
      if (isValidDraft(value)) {
        this.memory = { id: value.id, question: value.question, language: value.language, createdAt: value.createdAt, origin: value.origin ?? 'typed' };
        return { ...this.memory };
      }
      this.memory = null;
      if (raw) window.sessionStorage.removeItem(STORAGE_KEY);
      return null;
    } catch {
      this.storageUnavailable = true;
      return this.readMemory();
    }
  }

  save(question: string, language: Language, origin: LandingQuestionOrigin = 'typed'): LandingQuestionDraft | null {
    this.prepareNavigation();
    if (!question.trim()) {
      this.clear();
      return null;
    }
    if (question.length > GUEST_QUESTION_MAX_LENGTH || (language !== 'fr' && language !== 'en')
      || (origin !== 'typed' && origin !== 'example')) return null;
    const draft = { id: crypto.randomUUID(), question, language, createdAt: Date.now(), origin };
    this.memory = draft;
    if (!this.storageUnavailable) {
      try {
        window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(draft));
      } catch {
        this.storageUnavailable = true;
      }
    }
    this.notify();
    return { ...draft };
  }

  clearIfCurrent(id: string | null): void {
    if (id && this.read()?.id === id) this.clear();
  }

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => { this.listeners.delete(listener); };
  }

  private prepareNavigation(): void {
    if (this.navigationChecked || typeof window === 'undefined') return;
    this.navigationChecked = true;
    try {
      const entries = window.performance.getEntriesByType?.('navigation');
      const navigation = entries?.[0] as PerformanceNavigationTiming | undefined;
      if (navigation?.type === 'reload') this.clear();
    } catch { /* Missing navigation timing must not prevent editing. */ }
  }

  private readMemory(): LandingQuestionDraft | null {
    if (!isValidDraft(this.memory)) this.memory = null;
    return this.memory ? { ...this.memory } : null;
  }

  private clear(): void {
    this.memory = null;
    try {
      window.sessionStorage.removeItem(STORAGE_KEY);
    } catch {
      this.storageUnavailable = true;
    }
    this.notify();
  }

  private notify(): void {
    this.listeners.forEach((listener) => listener());
  }
}

export const landingQuestionDraftService = new LandingQuestionDraftService();
